import type { RouteObject } from 'react-router-dom';

import { MotionLazy } from '@mfw/components';
import { Outlet } from 'react-router-dom';

import BreadcrumbsProvider from '../../context/BreadcrumbsProvider';
import RouteErrorPage from '../../pages/RouteErrorPage';
import { ProgressBar } from '../../components';
import { AppProviders } from '../../context';
import { lazyRoute } from './lazyRoute';

/**
 * `/p/:orgSlug` and `/p/:orgSlug/:trainerSlug` — the one anonymous branch in
 * the FE router, mirroring `/privacyPolicy`'s exact provider stack
 * (`routes.tsx`: `BreadcrumbsProvider > MotionLazy > ProgressBar >
 * AppProviders > Outlet`) with `AuthenticatedGuard` left out
 * on purpose. This has to stay a TOP-LEVEL entry in `routes.tsx` — nested
 * under the `/` branch (which wraps everything in `AuthenticatedGuard`)
 * would bounce every anonymous visitor to the login screen, and the feature
 * would not exist.
 *
 * Both child paths resolve to the SAME lazy page — `PublicSitePage` reads
 * `orgSlug`/`trainerSlug` off `useParams()` itself, same split as the BE
 * controller's `getOrgPage`/`getTrainerPage`.
 */
export const publicSiteRoutes = {
  children: [
    { lazy: () => lazyRoute('/PublicSitePage'), path: ':orgSlug' },
    {
      lazy: () => lazyRoute('/PublicSitePage'),
      path: ':orgSlug/:trainerSlug',
    },
  ],
  element: (
    <BreadcrumbsProvider>
      <MotionLazy>
        <ProgressBar />
        {/* Both opt-outs say the same thing about this route: it belongs
            to the visitor's HOST, not to the visitor. The page's own
            language wins over the account's, and someone who followed a
            shared link is not being asked to install an app. */}
        <AppProviders canApplyAccountLocale={false} canPromptAppInstall={false}>
          <Outlet />
        </AppProviders>
      </MotionLazy>
    </BreadcrumbsProvider>
  ),
  errorElement: <RouteErrorPage />,
  path: '/p',
} satisfies RouteObject;
